import { ReactElement, useCallback, useMemo, useState } from 'react'
import { BufferGeometry, Mesh, Vector3 } from 'three'
import { Check, Close, ControlCamera, OpenWith, RotateRight } from '@mui/icons-material'
import { useModelSnapTargets } from '@editor/pages/StructuralPlanning/components/FloorplanDrawer/hooks'
import {
  DraggableLine,
  DrawController,
  ImmutableVector3,
  ScalingIndicator,
  TransientDrawState,
  useCameraStore,
} from '@modugen/ui-toolkit'
import { useControlStore, useModelStore } from '@editorStores'
import {
  useClickActionEnabled,
  useDisposeThreeObjectsOnUnmount,
  useSelectionMode,
} from '@editorHooks'
import SceneHud, { SceneHudAction } from '../SceneHud'
import { useTapelineCentersSnapTargets, useTapelineSnapTargets } from '../hooks'
import useScenePositionDrag, { ScenePosition } from '../hooks/useScenePositionDrag'
import AxisLabel from './components/AxisLabel'
import useStoreyImageOriginStore, {
  STOREY_IMAGE_ORIGIN_SELECTION_KEY,
  STOREY_IMAGE_ORIGIN_SELECTION_MESSAGE,
} from './store'
import useOriginRotateDrag from './useOriginRotateDrag'

const AXIS_LENGTH = 2

const X_AXIS_COLOR = '#e53935'
const Y_AXIS_COLOR = '#43a047'
const REFERENCE_POINT_COLOR = '#1e88e5'
const TRANSLATION_LINE_COLOR = '#bdbdbd'

interface Props {
  storey: string
  /**
   * Apply the picked transformation to the storey's floorplan image: the gizmo origin (picked on
   * the plan) gets translated onto the reference point, and the image rotates around the
   * reference point so the gizmo's x/y axes (rotated by `rotation` rad) align with the global
   * axes.
   */
  onApply: (referencePoint: ScenePosition, origin: ScenePosition, rotation: number) => void
}

/**
 * Scene element of the floorplan-image "In Szene" origin workflow: while the selection mode is
 * active, a first click picks the reference point (the fixed target the plan gets moved to) and a
 * second click places the x/y axes gizmo on the plan point that gets moved onto it. The gizmo can
 * then be dragged in the plane and rotated via its `SceneHud` (mirroring the IFC alignment HUD)
 * and applied, which reports both points through `onApply` so the image gets translated and
 * rotated accordingly.
 */
const StoreyImageOriginControl = ({ storey, onApply }: Props): ReactElement => {
  const referencePoint = useStoreyImageOriginStore(state => state.referencePoint)
  const origin = useStoreyImageOriginStore(state => state.origin)
  const rotation = useStoreyImageOriginStore(state => state.rotation)
  const setReferencePoint = useStoreyImageOriginStore(state => state.setReferencePoint)
  const setOrigin = useStoreyImageOriginStore(state => state.setOrigin)
  const reset = useStoreyImageOriginStore(state => state.reset)

  const { selectionModeKey, setSelectionMode, unsetSelectionMode } = useSelectionMode()
  const isSelecting = selectionModeKey === STOREY_IMAGE_ORIGIN_SELECTION_KEY

  const clickActionEnabled = useClickActionEnabled()
  const snapToCornersAndEdges = useControlStore(state => state.snapToCornersAndEdges)
  const snapToAngles = useControlStore(state => state.snapToAngles)
  const snapOrthogonal = useControlStore(state => state.snapOrthogonal)
  const setIsDrawingActive = useControlStore(state => state.setIsDrawingActive)
  const storeyBoundaries = useModelStore(state => state.model.storey_boundaries)
  const isOrthographic = useCameraStore(state => state.isOrthographic)

  const commitOrigin = useCallback(
    (_: string, position: ScenePosition) => setOrigin(position),
    [setOrigin],
  )

  const { startDrag, dragPreview, activeDragMode } = useScenePositionDrag({
    storeyBoundaries,
    setIsDrawingActive,
    onCommit: commitOrigin,
  })

  const { startRotateDrag, isRotating } = useOriginRotateDrag()

  // In the orthographic (2D) view the model is drawn as lines, so the DrawController has nothing
  // to raycast against and needs explicit snap targets (model elements, tapelines, …) instead.
  const modelSnapTargets = useModelSnapTargets({ xyOnly: isOrthographic })
  const tapelineSnapTargets = useTapelineSnapTargets(isOrthographic)
  const tapelineCenterTargets = useTapelineCentersSnapTargets(isOrthographic)

  // The global origin is a snap target too, so the plan can be aligned straight back onto it.
  const originSnapTarget = useMemo(
    () => [new Mesh(new BufferGeometry().setFromPoints([new Vector3(0, 0, 0)]))],
    [],
  )
  useDisposeThreeObjectsOnUnmount(originSnapTarget)

  const additionalSnapTargets = useMemo(
    () => [
      ...modelSnapTargets,
      ...tapelineSnapTargets,
      ...tapelineCenterTargets,
      ...originSnapTarget,
    ],
    [modelSnapTargets, tapelineSnapTargets, tapelineCenterTargets, originSnapTarget],
  )

  // Whether the rotate drag magnetically snaps to 90° multiples. Toggled from the HUD; on by
  // default.
  const [rotationSnapEnabled, setRotationSnapEnabled] = useState(true)

  // Follow the live drag position while the gizmo is being moved via the HUD.
  const displayedOrigin = dragPreview?.position ?? origin

  const handleDrawEnd = useCallback(
    (state: TransientDrawState) => {
      const { drawPoint } = state
      if (!drawPoint) return
      // Both points always sit on the plane of the storey being configured, regardless of what
      // geometry the pick actually landed on.
      const boundary = storeyBoundaries[storey]
      const z = boundary ? boundary[0] : drawPoint.z
      const point = { x: drawPoint.x, y: drawPoint.y, z }

      // First click picks the reference (target) point and moves the selection mode on to the
      // origin pick.
      if (!referencePoint) {
        setReferencePoint(point)
        setSelectionMode({
          key: STOREY_IMAGE_ORIGIN_SELECTION_KEY,
          message: STOREY_IMAGE_ORIGIN_SELECTION_MESSAGE,
        })
        return
      }

      // Second click places the gizmo on the plan point that gets moved onto the reference.
      setOrigin(point)
      unsetSelectionMode()
    },
    [
      referencePoint,
      setOrigin,
      setReferencePoint,
      setSelectionMode,
      storey,
      storeyBoundaries,
      unsetSelectionMode,
    ],
  )

  const axes = useMemo(() => {
    if (!displayedOrigin) return null
    const start = new ImmutableVector3(displayedOrigin.x, displayedOrigin.y, displayedOrigin.z)
    const xDirection = new ImmutableVector3(Math.cos(rotation), Math.sin(rotation), 0)
    const yDirection = new ImmutableVector3(-Math.sin(rotation), Math.cos(rotation), 0)
    return {
      x: { start, end: start.addScaledVector(xDirection, AXIS_LENGTH) },
      y: { start, end: start.addScaledVector(yDirection, AXIS_LENGTH) },
    }
  }, [displayedOrigin, rotation])

  const hudGroups = useMemo<SceneHudAction[][]>(() => {
    if (!displayedOrigin) return []
    return [
      [
        {
          key: 'move-xy',
          tooltip: 'In der Ebene verschieben',
          icon: <OpenWith fontSize="small" />,
          onPointerDown: event => startDrag('xy', storey, displayedOrigin, event),
          active: activeDragMode === 'xy',
          dataCy: 'storey-image-origin-move-xy',
        },
      ],
      [
        {
          key: 'rotate',
          tooltip: 'Um den Ursprung drehen',
          icon: <RotateRight fontSize="small" />,
          onPointerDown: event => startRotateDrag(event, rotationSnapEnabled),
          active: isRotating,
          dataCy: 'storey-image-origin-rotate',
        },
        {
          key: 'rotate-snap',
          tooltip: rotationSnapEnabled ? 'Einrasten (90°) aktiv' : 'Einrasten (90°) aus',
          icon: <ControlCamera fontSize="small" />,
          onClick: () => setRotationSnapEnabled(enabled => !enabled),
          toggled: rotationSnapEnabled,
          dataCy: 'storey-image-origin-rotate-snap',
        },
      ],
      [
        {
          key: 'apply',
          tooltip: 'Transformation anwenden',
          icon: <Check fontSize="small" />,
          onClick: () => {
            if (!referencePoint) return
            onApply(referencePoint, displayedOrigin, rotation)
          },
          dataCy: 'storey-image-origin-apply',
        },
        {
          key: 'cancel',
          tooltip: 'Abbrechen',
          icon: <Close fontSize="small" />,
          onClick: reset,
          variant: 'danger',
          dataCy: 'storey-image-origin-cancel',
        },
      ],
    ]
  }, [
    displayedOrigin,
    referencePoint,
    activeDragMode,
    startDrag,
    storey,
    onApply,
    reset,
    rotation,
    startRotateDrag,
    isRotating,
    rotationSnapEnabled,
  ])

  return (
    <>
      {isSelecting && (
        <DrawController
          enabled={clickActionEnabled}
          enableIndicator
          xyOnly={isOrthographic}
          snapToCornersAndEdges={snapToCornersAndEdges}
          snapToAngles={snapToAngles}
          orthoSnap={snapOrthogonal}
          additionalSnapTargets={additionalSnapTargets}
          onDrawEnd={handleDrawEnd}
        />
      )}

      {referencePoint && (
        <ScalingIndicator
          position={new Vector3(referencePoint.x, referencePoint.y, referencePoint.z)}
          color={REFERENCE_POINT_COLOR}
          visible
        />
      )}

      {displayedOrigin && axes && (
        <>
          <ScalingIndicator
            position={new Vector3(displayedOrigin.x, displayedOrigin.y, displayedOrigin.z)}
            color={X_AXIS_COLOR}
            visible
          />

          {/* Visualize the translation the gizmo origin will make onto the reference point. */}
          {referencePoint && (
            <DraggableLine
              line={{
                start: new ImmutableVector3(referencePoint.x, referencePoint.y, referencePoint.z),
                end: new ImmutableVector3(displayedOrigin.x, displayedOrigin.y, displayedOrigin.z),
              }}
              isVisible
              showIndicators={false}
              color={TRANSLATION_LINE_COLOR}
              showLengthIndicator={false}
              lineThreeProps={{ dashed: true, dashSize: 0.2, gapSize: 0.1 }}
            />
          )}

          <DraggableLine
            line={axes.x}
            isVisible
            showIndicators={false}
            color={X_AXIS_COLOR}
            showLengthIndicator={false}
          />
          <AxisLabel
            position={[axes.x.end.x, axes.x.end.y, axes.x.end.z]}
            color={X_AXIS_COLOR}
            label="x"
          />

          <DraggableLine
            line={axes.y}
            isVisible
            showIndicators={false}
            color={Y_AXIS_COLOR}
            showLengthIndicator={false}
          />
          <AxisLabel
            position={[axes.y.end.x, axes.y.end.y, axes.y.end.z]}
            color={Y_AXIS_COLOR}
            label="y"
          />

          <SceneHud
            position={new Vector3(displayedOrigin.x, displayedOrigin.y, displayedOrigin.z)}
            groups={hudGroups}
          />
        </>
      )}
    </>
  )
}

export default StoreyImageOriginControl
